import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SquareImageComponent } from '~/components';
import {
  InfiniteScrollDropdownProps,
  InfiniteScrollDropdownSelection,
} from '~/components/InfiniteScrollDropdown/InfiniteScrollDropdown';
import { OrganizationPageQueryOrganization } from '~/constants/graphqlTypes';
import {
  OrganizationsPageQuery,
  OrganizationType,
  useOrganizationQuery,
  useOrganizationsPageQuery,
} from '~/types/api.graphql';
import { getOrganizationInitials } from '~/utils/organization';

import { DropdownItemProps } from '../Dropdown/Dropdown';
import { StyledInfiniteScrollDropdown } from './OrganizationsDropdown.styles';

const PAGE_ITEMS_COUNT = 10;

export interface OrganizationsDropdownProps
  extends Omit<InfiniteScrollDropdownProps<OrganizationPageQueryOrganization>, 'onSelectionChange'> {
  organizationType?: OrganizationType;
  isOrganizationDisabled?: (org: OrganizationPageQueryOrganization) => boolean;
  onSelectionChange?: (
    selection: InfiniteScrollDropdownSelection,
    organization: OrganizationPageQueryOrganization,
  ) => void;
  onAutoSelected?: (organization: OrganizationPageQueryOrganization) => void;
  // When set (and no explicit `selectedIndex` is provided), preselects this org as soon as
  // it's available — pinning it into the list via its own fetch if it isn't already loaded.
  preselectedOrgId?: string;
}

const OrganizationsDropdown = (props: OrganizationsDropdownProps) => {
  const {
    isOrganizationDisabled,
    onAutoSelected,
    onSelectionChange,
    organizationType,
    preselectedOrgId,
    selectedIndex: selectedIndexProp,
    ...restProps
  } = props;
  const { t } = useTranslation();
  const [items, setItems] = useState<DropdownItemProps<OrganizationPageQueryOrganization>[]>([]);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [searchOrgInfiniteScroll, setSearchOrgInfiniteScroll] = useState('');
  const [localSelectedIndex, setLocalSelectedIndex] = useState<number>(props.selectedIndex);
  const selectedIndex = selectedIndexProp !== undefined ? selectedIndexProp : localSelectedIndex;
  const preselectHandledRef = useRef(false);

  const {
    refetch,
    loading: loadingFirst,
    data: orgsQueryData,
  } = useOrganizationsPageQuery({
    variables: {
      pageNumber: page,
      pageItemsCount: PAGE_ITEMS_COUNT,
      searchQuery: searchOrgInfiniteScroll,
      organizationType,
    },
    fetchPolicy: 'network-only',
    skip: page > 1,
  });

  const isPreselectedOrgLoaded = useMemo(
    () => !preselectedOrgId || items.some((item) => item.key === preselectedOrgId),
    [items, preselectedOrgId],
  );

  // The preselected org might not be on the currently loaded page(s), so fetch it individually
  // and pin it into the list once resolved (mirrors how the global org selector keeps the
  // active org's own record available regardless of pagination/search).
  const {
    data: preselectedOrgData,
    loading: preselectedOrgLoading,
    error: preselectedOrgError,
  } = useOrganizationQuery({
    variables: { organizationId: preselectedOrgId },
    skip: !preselectedOrgId || isPreselectedOrgLoaded,
    errorPolicy: 'all',
  });

  const disabledKeys = useMemo(() => {
    return items.filter((item) => item.disabled).map((item) => item.key);
  }, [items]);

  const buildOrgItem = useCallback(
    (organization: OrganizationPageQueryOrganization): DropdownItemProps<OrganizationPageQueryOrganization> => ({
      text: organization.name,
      value: organization,
      description: t('organization.settings.orgTypes.'.concat(String(organization.organizationType).toLowerCase())),
      key: organization.id,
      disabled: isOrganizationDisabled ? isOrganizationDisabled(organization) : false,
      icon: (
        <SquareImageComponent
          src={organization.imageUrl}
          squared
          text={getOrganizationInitials(organization.name || '')}
        />
      ),
    }),
    [isOrganizationDisabled, t],
  );

  const applyOrgsPage = useCallback(
    (dataOrgs: OrganizationsPageQuery) => {
      setItems((prevItems) => [...prevItems, ...(dataOrgs?.organizationsPage?.nodes?.map(buildOrgItem) || [])]);
      setHasMore(dataOrgs?.organizationsPage?.pageInfo?.hasNextPage);
      setPage((prevPage: number) => prevPage + 1);
    },
    [buildOrgItem],
  );

  // Keep reset before the data effect so a search/org change clears stale rows
  // before the next page result is appended.
  useEffect(() => {
    setPage(1);
    setItems([]);
    setHasMore(false);
    setLocalSelectedIndex(undefined);
  }, [searchOrgInfiniteScroll, organizationType]);

  useEffect(() => {
    if (orgsQueryData) {
      applyOrgsPage(orgsQueryData);
    }
    // Run once per Apollo data object. applyOrgsPage changes can re-append the same page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgsQueryData]);

  // Reset once the caller asks to preselect a different org (e.g. the globally active org changed).
  useEffect(() => {
    preselectHandledRef.current = false;
  }, [preselectedOrgId]);

  // Pin the individually-fetched preselected org into the list if it isn't already there.
  // If the fetch is forbidden/not-found (e.g. a stale or no-longer-accessible id), give up
  // quietly — no crash, no bogus item pinned, the field just stays unselected.
  useEffect(() => {
    if (!preselectedOrgId || preselectHandledRef.current || isPreselectedOrgLoaded || preselectedOrgLoading) return;
    const organization = preselectedOrgData?.organization as OrganizationPageQueryOrganization;
    if (!organization || preselectedOrgError) {
      preselectHandledRef.current = true;
      return;
    }
    setItems((prevItems) =>
      prevItems.some((item) => item.key === organization.id) ? prevItems : [buildOrgItem(organization), ...prevItems],
    );
  }, [
    preselectedOrgId,
    preselectedOrgData,
    preselectedOrgError,
    preselectedOrgLoading,
    isPreselectedOrgLoaded,
    buildOrgItem,
  ]);

  const handleItemSelection = useCallback(
    (selection) => {
      const selectedIndex = selection as number;
      setLocalSelectedIndex(selectedIndex);
      onSelectionChange?.(selectedIndex, items[selectedIndex].value);
    },
    [items, onSelectionChange],
  );

  // Use a ref so the auto-selection effect only fires when items change,
  // not every time the parent re-renders and recreates onSelectionChange
  // (which would recreate handleItemSelection, causing an infinite render loop).
  const handleItemSelectionRef = useRef(handleItemSelection);
  handleItemSelectionRef.current = handleItemSelection;

  const onAutoSelectedRef = useRef(onAutoSelected);
  onAutoSelectedRef.current = onAutoSelected;

  // Auto-select the preselected org once it's present in `items` (whether via the normal
  // paginated fetch or pinned above). Skipped when an explicit `selectedIndex` is provided,
  // or when the matching item is disabled.
  useEffect(() => {
    if (!preselectedOrgId || preselectHandledRef.current || selectedIndexProp !== undefined) return;
    const index = items.findIndex((item) => item.key === preselectedOrgId);
    if (index < 0 || items[index].disabled) return;
    preselectHandledRef.current = true;
    handleItemSelectionRef.current(index);
  }, [items, preselectedOrgId, selectedIndexProp]);

  useEffect(() => {
    if (preselectHandledRef.current) return;
    if (items?.length === 1 && !items?.[0]?.disabled) {
      handleItemSelectionRef.current(0);
      onAutoSelectedRef.current?.(items[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const loadingFirstPage = loadingFirst && !items.length;

  const handleLoadMore = () => {
    if (!hasMore || loadingFirstPage || loadingMore) {
      return;
    }

    void (async () => {
      setLoadingMore(true);
      try {
        const result = await refetch({
          pageNumber: page,
          pageItemsCount: PAGE_ITEMS_COUNT,
          searchQuery: searchOrgInfiniteScroll,
          organizationType,
        });
        if (result.data) applyOrgsPage(result.data);
      } finally {
        setLoadingMore(false);
      }
    })();
  };

  return (
    <StyledInfiniteScrollDropdown
      {...restProps}
      items={items}
      disabledKeys={disabledKeys}
      onSelectionChange={handleItemSelection}
      selectedIndex={selectedIndex}
      setSearchOrgInfiniteScroll={setSearchOrgInfiniteScroll}
      searchOrgInfiniteScroll={searchOrgInfiniteScroll}
      hasMore={hasMore}
      loadMore={handleLoadMore}
      loadingFirst={loadingFirstPage}
      loadingMore={loadingMore}
      disallowEmptySelection
    />
  );
};

export default OrganizationsDropdown;
